import type { CardId, MiceNeedsQuery, ProjectId, SquadId } from '@voll-intelligence/domain';
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
    },
    mutations: { retry: 0 },
  },
});

/**
 * Centralised query-key factories.
 *
 * Pattern follows TkDodo's "Effective React Query Keys" (TanStack maintainer):
 * each feature exposes a hierarchical factory whose `all` root prefixes every
 * derived key, so `invalidateQueries({ queryKey: feature.all })` cascades to
 * every cached entry for that feature.
 *
 * Always import via the registry — do not inline string-array keys at call
 * sites. Adding a new feature query: extend this file, never duplicate keys.
 */

export const queryKeys = {
  me: {
    all: ['me'] as const,
    permissions: (orgId: string) => ['me', 'permissions', orgId] as const,
  },
  projects: {
    all: ['support', 'projects'] as const,
    list: (orgId: string) => ['support', 'projects', 'list', orgId] as const,
    detail: (orgId: string, projectId: ProjectId) =>
      ['support', 'projects', 'detail', orgId, projectId] as const,
    members: (orgId: string, projectId: ProjectId) =>
      ['support', 'projects', 'members', orgId, projectId] as const,
    assignableUsers: (orgId: string, projectId: ProjectId) =>
      ['support', 'projects', 'assignable-users', orgId, projectId] as const,
  },
  squads: {
    all: ['support', 'squads'] as const,
    detail: (orgId: string, projectId: ProjectId, squadId: SquadId) =>
      ['support', 'squads', 'detail', orgId, projectId, squadId] as const,
    byId: (orgId: string, squadId: SquadId) =>
      ['support', 'squads', 'by-id', orgId, squadId] as const,
  },
  cards: {
    all: ['cards'] as const,
    detail: (orgId: string, cardId: CardId) => ['cards', 'detail', orgId, cardId] as const,
    byConversation: (orgId: string, conversationId: string) =>
      ['cards', 'by-conversation', orgId, conversationId] as const,
    /**
     * Audit key factory. When `filters` is omitted the result is the
     * 4-element prefix `['cards', 'audit', orgId, cardId]`, so passing
     * that to `invalidateQueries` matches every filtered variant for
     * the card (TanStack Query treats keys as prefixes by default).
     * When called with a filters record, the record is appended for the
     * concrete `useQuery` lookup.
     */
    audit: (orgId: string, cardId: CardId, filters?: Record<string, unknown>): readonly unknown[] =>
      filters === undefined
        ? ['cards', 'audit', orgId, cardId]
        : ['cards', 'audit', orgId, cardId, filters],
    tasks: (orgId: string, cardId: CardId) => ['cards', 'tasks', orgId, cardId] as const,
  },
  search: {
    query: (orgId: string, q: string, channels: readonly string[], period: string) =>
      [
        'search',
        orgId,
        q,
        [...channels].sort((a, b) => a.localeCompare(b, 'en')).join(','),
        period,
      ] as const,
  },
  customerSearch: {
    query: (orgId: string, q: string) => ['customer-search', orgId, q] as const,
  },
  customerDossier: {
    detail: (orgId: string, id: string) => ['customer-dossier', orgId, id] as const,
  },
  trips: {
    all: ['trips'] as const,
    detail: (orgId: string, customerId: string, tripId: string) =>
      ['trips', 'detail', orgId, customerId, tripId] as const,
    list: (orgId: string, customerId: string) => ['trips', 'list', orgId, customerId] as const,
  },
  boards: {
    all: ['boards'] as const,
    conversation: (orgId: string) => ['boards', 'conversation', orgId] as const,
    tasks: (orgId: string) => ['boards', 'tasks', orgId] as const,
    squadConversation: (orgId: string, squadId: SquadId) =>
      ['boards', 'squad', 'conversation', orgId, squadId] as const,
    squadTasks: (orgId: string, squadId: SquadId) =>
      ['boards', 'squad', 'tasks', orgId, squadId] as const,
    squadMyTasks: (orgId: string, squadId: SquadId) =>
      ['boards', 'squad', 'my-tasks', orgId, squadId] as const,
  },
  orgBoardConfig: (orgId: string) => ['org-board-config', orgId] as const,
  triage: {
    all: ['triage'] as const,
    config: (orgId: string) => ['triage', 'config', orgId] as const,
    inbox: (orgId: string) => ['triage', 'inbox', orgId] as const,
  },
  overview: {
    all: ['overview'] as const,
    snapshot: (orgId: string) => ['overview', 'snapshot', orgId] as const,
    sidebarCounts: (orgId: string) => ['overview', 'sidebar-counts', orgId] as const,
  },
  conversation: {
    thread: (conversationId: string) => ['conversation', conversationId, 'thread'] as const,
    meta: (conversationId: string) => ['conversation', conversationId, 'meta'] as const,
  },
  alerts: {
    all: ['alerts'] as const,
    flights: (orgId: string, f: { severity: string; vip: boolean; q: string }) =>
      ['alerts', 'flights', orgId, f.severity, f.vip, f.q] as const,
    categories: (orgId: string) => ['alerts', 'categories', orgId] as const,
    trip: (orgId: string, alertKey: string) => ['alerts', 'trip', orgId, alertKey] as const,
    traveler: (orgId: string, alertKey: string) => ['alerts', 'traveler', orgId, alertKey] as const,
  },
  mice: {
    all: ['mice'] as const,
    projects: (orgId: string) => ['mice', orgId, 'projects'] as const,
    project: (orgId: string, projectId: string) => ['mice', orgId, 'projects', projectId] as const,
    needs: (orgId: string, projectId: string, query: MiceNeedsQuery) =>
      ['mice', orgId, 'projects', projectId, 'needs', query] as const,
    travelers: (orgId: string, projectId: string, filter: Readonly<Record<string, string>>) =>
      ['mice', orgId, 'projects', projectId, 'travelers', filter] as const,
    traveler: (orgId: string, projectId: string, travelerId: string) =>
      ['mice', orgId, 'projects', projectId, 'traveler', travelerId] as const,
    activity: (orgId: string, projectId: string) =>
      ['mice', orgId, 'projects', projectId, 'activity'] as const,
    travelerCostCenters: (orgId: string, projectId: string) =>
      ['mice', orgId, 'projects', projectId, 'traveler-cost-centers'] as const,
    travelerCostCenterOptions: (orgId: string, projectId: string) =>
      ['mice', orgId, 'projects', projectId, 'traveler-cost-centers', 'options'] as const,
    flightOptions: (
      orgId: string,
      projectId: string,
      needId: string,
      version: number,
      direction: 'OUTWARD' | 'RETURN',
    ) =>
      ['mice', orgId, 'projects', projectId, 'flight-options', needId, version, direction] as const,
    orderVouchers: (orgId: string, projectId: string, orderId: string) =>
      ['mice', orgId, 'projects', projectId, 'orders', 'vouchers', orderId] as const,
    clients: (orgId: string, q: string) => ['mice', orgId, 'clients', q] as const,
    workspaceOptions: (orgId: string, clientId: string, workspaceId: string) =>
      ['mice', orgId, 'workspaces', clientId, workspaceId] as const,
    debitCompanyOptions: (
      orgId: string,
      clientId: string,
      workspaceId: string,
      companyId: string,
    ) =>
      ['mice', orgId, 'workspaces', clientId, workspaceId, 'debit-companies', companyId] as const,
    requesters: (orgId: string, clientId: string, workspaceId: string, q: string) =>
      ['mice', orgId, 'requester-search', clientId, workspaceId, q] as const,
  },
  viewAs: {
    all: ['view-as'] as const,
    search: (orgId: string, q: string) => ['view-as', 'search', orgId, q] as const,
  },
  travelerAccess: {
    all: ['traveler-access'] as const,
    list: (orgId: string, filter: Readonly<Record<string, string>>) =>
      ['traveler-access', orgId, filter] as const,
  },
  cardDrawer: {
    all: ['card-drawer'] as const,
    detail: (orgId: string, cardId: CardId) => ['card-drawer', orgId, cardId, 'detail'] as const,
    notes: (orgId: string, cardId: CardId) => ['card-drawer', orgId, cardId, 'notes'] as const,
    history: (orgId: string, cardId: CardId) => ['card-drawer', orgId, cardId, 'history'] as const,
    // `customerPublicId` na chave: o queryFn depende do customer do card, então
    // a chave muda junto se o customer de um card mudar (sem servir o viajante
    // anterior do cache).
    customerProfile: (orgId: string, cardId: CardId, customerPublicId: string) =>
      ['card-drawer', orgId, cardId, 'customer-profile', customerPublicId] as const,
  },
} as const;
